import {
  INVOICE_STATUS_DEFINITIONS,
  PAYMENT_METHOD_DEFINITIONS,
  ZATCA_IMPLEMENTATION_STATUS,
  DEFAULT_INVOICE_PRESENTATION,
  LOGO_PIXEL_HEIGHT,
  QR_PIXEL_SIZE,
  invoiceDocumentTitle,
  type InvoicePresentation,
  type InvoiceViewModel,
  type PaymentMethod,
} from "@laundrypro/shared";
import { QrCode } from "./QrCode";
import { money } from "../../lib/format";

/**
 * The printable invoice document, in three formats from ONE component.
 *
 * A4, 80mm and 58mm differ only in what they can afford to show and how wide
 * they are. They must never differ in what they SAY, so all three read the
 * same `InvoiceViewModel` and none of them computes anything: every figure
 * here is already a number the server stored. If a format needs a value that
 * isn't on the view model, the fix is to add it to the view model — the moment
 * a template does arithmetic, the receipt in the customer's hand can disagree
 * with the PDF in the accountant's folder.
 *
 * Physical widths come from the paper, not from taste: 80mm and 58mm rolls
 * have ~72mm and ~48mm of printable width once the head margins are taken off.
 */

export type InvoiceFormat = "a4" | "thermal80" | "thermal58";

const FORMAT_WIDTH: Record<InvoiceFormat, string> = {
  a4: "210mm",
  thermal80: "72mm",
  thermal58: "48mm",
};

export function InvoiceDocument({
  invoice,
  format,
  language,
  logoUrl,
  footerText,
  presentation = DEFAULT_INVOICE_PRESENTATION,
}: {
  invoice: InvoiceViewModel;
  format: InvoiceFormat;
  language: "ar" | "en";
  logoUrl?: string | null;
  footerText?: string | null;
  /**
   * Owner-controlled presentation — the SAME contract the server PDF template
   * consumes, so a setting cannot take effect on one surface and not another.
   * Defaults reproduce the pre-configurable rendering exactly.
   */
  presentation?: InvoicePresentation;
}) {
  const p = presentation;
  const ar = language === "ar";
  const thermal = format !== "a4";
  const narrow = format === "thermal58";

  const t = invoice.totals;
  const title = invoiceDocumentTitle(invoice.status, invoice.isSimplified);
  const statusDef = INVOICE_STATUS_DEFINITIONS[invoice.status];
  const currency = ar ? invoice.currency.labelAr : invoice.currency.labelEn;
  const sellerName = ar ? invoice.seller.nameAr : invoice.seller.nameEn;
  const sellerAddress = ar ? invoice.seller.addressAr : invoice.seller.addressEn;
  // An anonymous walk-in has no customer at all. Driven by the SNAPSHOT
  // fields, so an invoice issued to a named customer keeps showing them even
  // if that customer record is edited or removed later.
  const hasCustomer = Boolean(
    invoice.customer.name || invoice.customer.mobile || invoice.customer.vatNumber,
  );

  const showNotes = p.showNotes && Boolean(invoice.notes);

  // Logical alignment, so "start" is the right edge in Arabic and the left in
  // English without a second code path.
  const logoAlignStyle =
    p.logoAlign === "center"
      ? { marginInline: "auto" }
      : p.logoAlign === "end"
        ? { marginInlineStart: "auto" }
        : { marginInlineEnd: "auto" };

  /**
   * The QR, rendered once and PLACED by the owner's setting.
   *
   * Built as a value rather than inlined twice: two copies of this JSX would be
   * two things to keep in step, and the whole point of this pass is that a
   * setting produces one result everywhere.
   */
  const qrBlock =
    p.showQr && invoice.zatcaQrPayload ? (
      <section
        data-qr-position={p.qrPosition}
        className={`flex flex-col items-center ${thermal ? "mt-3" : "mt-6"}`}
      >
        <QrCode
          payload={invoice.zatcaQrPayload}
          // Thermal narrows the owner's choice to what the roll can physically
          // carry — a "large" QR on 58mm would run off the paper.
          size={Math.min(QR_PIXEL_SIZE[p.qrSize], narrow ? 96 : thermal ? 116 : 999)}
          title={ar ? "رمز الفاتورة" : "Invoice QR"}
        />
        {/* Says what the code IS without claiming what it is not. The exact
            wording is derived from the implementation status object, so the
            claim printed on a tax document cannot drift from the code. */}
        <p className={`mt-1 text-center text-ink-500 ${thermal ? "text-[8px]" : "text-[10px]"}`}>
          {ar ? "رمز الفاتورة الضريبية المبسطة" : "Simplified tax invoice QR"}
        </p>
      </section>
    ) : null;

  const fmtDate = (iso: string | null) => {
    if (!iso) return "—";
    // Latin digits even in Arabic: Saudi financial documents expect them, and
    // the shared `.tabular-nums` rule already forces `lnum`.
    return new Date(iso).toLocaleString(ar ? "ar-SA-u-nu-latn" : "en-GB", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <article
      dir={ar ? "rtl" : "ltr"}
      lang={ar ? "ar" : "en"}
      data-invoice-format={format}
      className={`lp-invoice-doc mx-auto bg-white text-ink-900 ${thermal ? "lp-invoice-thermal" : "lp-invoice-a4"}`}
      style={{ width: FORMAT_WIDTH[format] }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className={thermal ? "text-center" : "flex items-start justify-between gap-6"}>
        <div className={thermal ? "" : "min-w-0"}>
          {p.showLogo && logoUrl && (
            <img
              src={logoUrl}
              alt=""
              // Height and alignment come from the owner's setting, not the
              // format. Thermal still centres by default because a receipt
              // roll has no other sensible resting place.
              style={{ maxHeight: `${LOGO_PIXEL_HEIGHT[p.logoSize]}px`, ...logoAlignStyle }}
              className={thermal ? "mb-2 block" : "mb-3 block"}
            />
          )}
          {p.showBusinessName && (
            <h1 className={`font-bold ${thermal ? "text-[13px]" : "text-lg"}`}>{sellerName}</h1>
          )}
          {p.showAddress && sellerAddress && (
            <p className={`text-ink-600 ${thermal ? "text-[10px]" : "text-xs"}`}>{sellerAddress}</p>
          )}
          {invoice.seller.phone && (
            <p className={`tabular-nums text-ink-600 ${thermal ? "text-[10px]" : "text-xs"}`}>
              {invoice.seller.phone}
            </p>
          )}
          {p.showVatNumber && invoice.seller.vatNumber && (
            <p className={`tabular-nums text-ink-600 ${thermal ? "text-[10px]" : "text-xs"}`}>
              {ar ? "الرقم الضريبي: " : "VAT No: "}
              {invoice.seller.vatNumber}
            </p>
          )}
          {invoice.seller.crNumber && !thermal && (
            <p className="tabular-nums text-xs text-ink-600">
              {ar ? "السجل التجاري: " : "CR No: "}
              {invoice.seller.crNumber}
            </p>
          )}
        </div>

        <div className={thermal ? "mt-2" : "shrink-0 text-end"}>
          <h2 className={`font-bold ${thermal ? "text-[12px]" : "text-base"}`}>
            {ar ? title.ar : title.en}
          </h2>
          {invoice.invoiceRef && (
            <p className={`tabular-nums font-semibold ${thermal ? "text-[12px]" : "text-sm"}`}>
              {invoice.invoiceRef}
            </p>
          )}
          {/* A voided document must announce itself in text, not only by a
              colour a monochrome thermal head cannot print. */}
          {invoice.status === "VOIDED" && (
            <p className={`font-bold text-danger ${thermal ? "text-[12px]" : "text-sm"}`}>
              {ar ? statusDef.labelAr : statusDef.labelEn}
            </p>
          )}
        </div>
      </header>

      {/* ── QR (top placement) ──────────────────────────────────────────── */}
      {p.qrPosition === "top" && qrBlock}

      <Rule thermal={thermal} />

      {/* ── Parties and dates ───────────────────────────────────────────── */}
      <section className={thermal ? "space-y-0.5" : "grid grid-cols-2 gap-6"}>
        {/* Omitted ENTIRELY for an anonymous walk-in rather than printed as
            "Customer —". A dash reads as missing data on a tax document; no
            block at all reads as what it is: a counter sale with no named
            buyer. The dates column then simply takes the first grid slot. */}
        {p.showCustomer && hasCustomer && (
          <dl className={thermal ? "space-y-0.5" : "space-y-1"}>
            {invoice.customer.name && (
              <Row thermal={thermal} label={ar ? "العميل" : "Customer"} value={invoice.customer.name} />
            )}
            {invoice.customer.mobile && (
              <Row thermal={thermal} label={ar ? "الجوال" : "Mobile"} value={invoice.customer.mobile} numeric />
            )}
            {invoice.customer.vatNumber && (
              <Row thermal={thermal} label={ar ? "الرقم الضريبي للعميل" : "Customer VAT"} value={invoice.customer.vatNumber} numeric />
            )}
          </dl>
        )}
        <dl className={thermal ? "space-y-0.5" : "space-y-1"}>
          {p.showDatetime && (
            <Row thermal={thermal} label={ar ? "تاريخ الإصدار" : "Issued"} value={fmtDate(invoice.issuedAt ?? invoice.createdAt)} numeric />
          )}
          {invoice.dueAt && (
            <Row thermal={thermal} label={ar ? "تاريخ الاستحقاق" : "Due"} value={fmtDate(invoice.dueAt)} numeric />
          )}
          {invoice.orderRef && (
            <Row thermal={thermal} label={ar ? "الطلب" : "Order"} value={invoice.orderRef} numeric />
          )}
        </dl>
      </section>

      <Rule thermal={thermal} />

      {/* ── Lines ───────────────────────────────────────────────────────── */}
      {thermal ? (
        <ul className="space-y-1.5">
          {invoice.lines.map((line) => (
            <li key={line.id} className="text-[10px] leading-tight">
              <div className="font-medium">
                {ar ? line.itemNameAr : line.itemNameEn} — {ar ? line.serviceNameAr : line.serviceNameEn}
              </div>
              <div className="flex justify-between tabular-nums">
                <span>
                  {line.quantity} × {money(line.unitPriceMinor)}
                </span>
                <span className="font-semibold">{money(line.lineTotalMinor)}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-ink-900 text-start">
              <th className="py-2 text-start font-semibold">{ar ? "الصنف" : "Item"}</th>
              <th className="py-2 text-start font-semibold">{ar ? "الخدمة" : "Service"}</th>
              <th className="py-2 text-end font-semibold">{ar ? "الكمية" : "Qty"}</th>
              <th className="py-2 text-end font-semibold">{ar ? "السعر" : "Unit"}</th>
              <th className="py-2 text-end font-semibold">{ar ? "الضريبة" : "VAT"}</th>
              <th className="py-2 text-end font-semibold">{ar ? "الإجمالي" : "Total"}</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line) => (
              <tr key={line.id} className="border-b border-ink-200">
                <td className="py-1.5">{ar ? line.itemNameAr : line.itemNameEn}</td>
                <td className="py-1.5">{ar ? line.serviceNameAr : line.serviceNameEn}</td>
                <td className="py-1.5 text-end tabular-nums">{line.quantity}</td>
                <td className="py-1.5 text-end tabular-nums">{money(line.unitPriceMinor)}</td>
                <td className="py-1.5 text-end tabular-nums">{money(line.taxMinor)}</td>
                <td className="py-1.5 text-end tabular-nums font-medium">{money(line.lineTotalMinor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Rule thermal={thermal} />

      {/* ── Totals ──────────────────────────────────────────────────────── */}
      <section className={thermal ? "space-y-0.5" : "ms-auto w-64 space-y-1"}>
        <Total thermal={thermal} label={ar ? "الإجمالي قبل الضريبة" : "Subtotal (excl. VAT)"} value={money(t.subtotalMinor)} />
        {t.discountMinor > 0 && (
          <Total thermal={thermal} label={ar ? "الخصم" : "Discount"} value={`− ${money(t.discountMinor)}`} />
        )}
        {invoice.tax.vatEnabled && (
          <Total
            thermal={thermal}
            label={`${ar ? "ضريبة القيمة المضافة" : "VAT"} (${invoice.tax.ratePercent})`}
            value={money(t.taxMinor)}
          />
        )}
        <Total thermal={thermal} strong label={ar ? "الإجمالي" : "Total"} value={`${money(t.totalMinor)} ${currency}`} />
        {t.amountPaidMinor !== 0 && (
          <Total thermal={thermal} label={ar ? "المدفوع" : "Paid"} value={money(t.amountPaidMinor)} />
        )}
        {t.balanceDueMinor !== 0 && (
          <Total thermal={thermal} strong label={ar ? "المتبقي" : "Balance due"} value={`${money(t.balanceDueMinor)} ${currency}`} />
        )}
      </section>

      {/* Multi-rate VAT summary, grouped from the stored per-line figures.
          Only shown when the document genuinely spans more than one rate —
          otherwise it repeats the totals block above. */}
      {p.showTaxDetail && invoice.tax.byRate.length > 1 && !thermal && (
        <section className="mt-4">
          <h3 className="mb-1 text-xs font-semibold">{ar ? "ملخص الضريبة" : "VAT summary"}</h3>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-ink-300">
                <th className="py-1 text-start">{ar ? "النسبة" : "Rate"}</th>
                <th className="py-1 text-end">{ar ? "الوعاء" : "Taxable"}</th>
                <th className="py-1 text-end">{ar ? "الضريبة" : "VAT"}</th>
              </tr>
            </thead>
            <tbody>
              {invoice.tax.byRate.map((r) => (
                <tr key={r.rateBps} className="border-b border-ink-150">
                  <td className="py-1 tabular-nums">{(r.rateBps / 100).toFixed(2)}%</td>
                  <td className="py-1 text-end tabular-nums">{money(r.baseMinor)}</td>
                  <td className="py-1 text-end tabular-nums">{money(r.taxMinor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* ── Payments ────────────────────────────────────────────────────── */}
      {p.showPaymentMethod && invoice.payments.length > 0 && (
        <section className={thermal ? "mt-2" : "mt-4"}>
          <h3 className={`font-semibold ${thermal ? "text-[10px]" : "mb-1 text-xs"}`}>
            {ar ? "المدفوعات" : "Payments"}
          </h3>
          <ul className={thermal ? "space-y-0.5 text-[10px]" : "space-y-0.5 text-xs"}>
            {invoice.payments.map((p) => (
              <li key={p.id} className="flex justify-between gap-2 tabular-nums">
                <span>
                  {ar
                    ? PAYMENT_METHOD_DEFINITIONS[p.method as PaymentMethod].ar
                    : PAYMENT_METHOD_DEFINITIONS[p.method as PaymentMethod].en}
                  {" · "}
                  {fmtDate(p.receivedAt)}
                </span>
                <span className={p.amountMinor < 0 ? "text-danger" : ""}>{money(p.amountMinor)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── QR (bottom placement) ───────────────────────────────────────── */}
      {p.qrPosition === "bottom" && qrBlock}

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      {(footerText || showNotes) && (
        <footer className={`text-center text-ink-600 ${thermal ? "mt-2 text-[9px]" : "mt-6 text-xs"}`}>
          {showNotes && <p className="whitespace-pre-line">{invoice.notes}</p>}
          {footerText && <p className="mt-1 whitespace-pre-line">{footerText}</p>}
        </footer>
      )}

      {invoice.status === "DRAFT" && (
        <p className={`mt-3 text-center font-semibold text-warning ${thermal ? "text-[9px]" : "text-xs"}`}>
          {ar ? "مسودة — غير صالحة كمستند ضريبي" : "DRAFT — not valid as a tax document"}
        </p>
      )}

      {/* The compliance boundary, printed on the document itself. */}
      <p className={`mt-2 text-center text-ink-400 ${thermal ? "text-[7px]" : "text-[9px]"}`}>
        {ar ? ZATCA_IMPLEMENTATION_STATUS.statementAr : ZATCA_IMPLEMENTATION_STATUS.statementEn}
      </p>
    </article>
  );
}

function Rule({ thermal }: { thermal: boolean }) {
  return <hr className={`border-ink-300 ${thermal ? "my-2 border-dashed" : "my-4"}`} />;
}

function Row({
  label, value, thermal, numeric = false,
}: { label: string; value: string; thermal: boolean; numeric?: boolean }) {
  return (
    <div className={`flex justify-between gap-2 ${thermal ? "text-[10px]" : "text-xs"}`}>
      <dt className="text-ink-500">{label}</dt>
      <dd className={numeric ? "tabular-nums" : ""}>{value}</dd>
    </div>
  );
}

function Total({
  label, value, thermal, strong = false,
}: { label: string; value: string; thermal: boolean; strong?: boolean }) {
  return (
    <div
      className={`flex justify-between gap-3 ${thermal ? "text-[10px]" : "text-sm"} ${
        strong ? "border-t-2 border-ink-900 pt-1 font-bold" : ""
      }`}
    >
      <span className={strong ? "" : "text-ink-600"}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
